import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  DisclosureLink,
  DownloadButton,
  SearchField, EmptyState, Panel, ResultTable, SegmentedControl, StatusChip, type ResultColumn,
} from '../../../../ui-kit';
import { formatRecordValue, recordSortValue } from '../lookupFormat';
import type { LookupRecordsBlock } from '../../../../types/lookupDashboard';

interface RecordsPanelProps {
  block: LookupRecordsBlock;
  loading?: boolean;
  /** Said under the table: where the rows come from and what is left out. */
  footer?: React.ReactNode;
  /** Downloads the list on screen; `segment` names it when the records split into lists. */
  onDownload: (segment?: string) => void;
  downloading?: boolean;
  /** Problem with the download, shown along the foot. */
  problem?: string | null;
  /**
   * A row's evidence, opened under the row from the link in a `detail`
   * column — the way the Scorecard opens an agent breakdown.
   */
  renderDetail?: (row: Record<string, unknown>, columnKey: string, onClose: () => void) => React.ReactNode;
}

/**
 * Every matching record, sortable and paged, with the board's Excel download.
 *
 * A block with `segments` splits its records into lists — "Call today",
 * "Not enough history" — picked with a switch whose labels carry
 * their counts. Picking one swaps the rows, the count, the info and the
 * download; the first list with anything in it is shown first.
 */
export default function RecordsPanel({ block, loading, footer, onDownload, downloading, problem, renderDetail }: RecordsPanelProps) {
  const segments = block.segments ?? [];
  const [picked, setPicked] = React.useState<string | null>(null);
  const segment = segments.find((s) => s.key === picked) ?? segments.find((s) => s.count > 0) ?? segments[0] ?? null;
  const flagged = (v: unknown) => v !== null && v !== undefined && v !== '';
  // One row's evidence open at a time; a fresh load closes it.
  const [openRow, setOpenRow] = React.useState<Record<string, unknown> | null>(null);
  // Hidden columns are for the chat; a `detail` column needs somewhere to open.
  const hasDetail = Boolean(renderDetail);
  const shownColumns = React.useMemo(
    () => block.columns.filter((c) => !c.hidden && (c.format !== 'detail' || hasDetail)),
    [block.columns, hasDetail],
  );
  const detailColumn = shownColumns.find((c) => c.format === 'detail');
  const columns: ResultColumn[] = shownColumns.map((c) => ({
    key: c.key,
    label: c.label,
    numeric: c.format === 'int' || c.format === 'money0' || c.format === 'money2',
    format: (v) => (c.format === 'flag' && !flagged(v) ? c.empty_label ?? '—' : formatRecordValue(v, c.format)),
    sortValue: recordSortValue(c.format),
    ...(c.sticky && { sticky: true }),
    ...(c.width && { width: c.width }),
    ...(c.wrap && { wrap: true }),
    // A flag reads as a state: the record's own words in a warning chip, or
    // the column's all-clear ("Clean") when there is nothing to flag.
    ...(c.info && { info: c.info }),
    // A row's evidence: a link that opens it under the row, never the
    // evidence itself. The cell holds the whole row, so the panel can name
    // whose it is.
    ...(c.format === 'detail' && {
      numeric: false,
      format: (v: unknown) => ((v as Record<string, unknown>)[c.key] ? c.action_label ?? 'View' : '—'),
      sortValue: (v: unknown) => ((v as Record<string, unknown>)[c.key] ? 1 : 0),
      render: (v: unknown) => {
        const row = v as Record<string, unknown>;
        const isOpen = openRow === row;
        return row[c.key] ? (
          <DisclosureLink open={isOpen} onClick={() => setOpenRow(isOpen ? null : row)}>
            {c.action_label ?? 'View'}
          </DisclosureLink>
        ) : (
          <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Box>
        );
      },
    }),
    ...(c.format === 'flag' && {
      render: (v: unknown) => (flagged(v)
        ? <StatusChip tone="bad" label={String(v)} />
        : <StatusChip tone="good" label={c.empty_label ?? 'None'} plain />),
    }),
  }));
  // Built once per load: the table goes back to its first page whenever it is
  // handed new rows, so rows rebuilt on every redraw would undo each page turn.
  const segmentKey = segment?.key ?? null;
  const shown = React.useMemo(
    () => (segmentKey ? block.rows.filter((r) => r.segment === segmentKey) : block.rows),
    [block.rows, segmentKey],
  );
  // The search box narrows the list to rows whose search columns hold what
  // was typed — a contact ID, part of a policy number, a name — in any case.
  const [query, setQuery] = React.useState('');
  const needle = query.trim().toLowerCase();
  const searchColumns = block.search?.columns;
  const found = React.useMemo(
    () => (needle && searchColumns
      ? shown.filter((r) => searchColumns.some((k) => String(r[k] ?? '').toLowerCase().includes(needle)))
      : shown),
    [shown, needle, searchColumns],
  );
  const rows = React.useMemo(
    () => found.map((r) => shownColumns.map((c) => (c.format === 'detail' ? r : r[c.key]))),
    [found, shownColumns],
  );
  const total = segment ? segment.count : block.total;
  const truncated = total > shown.length;
  const flagIndexes = shownColumns.flatMap((c, i) => (c.format === 'flag' ? [i] : []));
  const rowTone = flagIndexes.length
    ? (cells: unknown[]) => (flagIndexes.some((i) => flagged(cells[i])) ? 'danger' as const : null)
    : undefined;

  const table = (fill: boolean) => (needle && found.length === 0 ? (
    <EmptyState height={200} message={`No clients match "${query.trim()}".`} />
  ) : segment && total === 0 ? (
    <EmptyState height={200} message={`No clients on this list${segment.label ? `: ${segment.label.toLowerCase()}` : ''}.`} />
  ) : (
    <ResultTable
      columns={columns}
      rows={rows}
      pageSize={50}
      // Scrolls inside its panel, like every record list: rows of different
      // heights would otherwise move the page with each page turn.
      maxHeight={fill ? undefined : 640}
      fill={fill}
      ariaLabel={block.title ?? 'Records'}
      rowTone={rowTone}
      renderRowDetail={detailColumn && renderDetail ? (cells) => {
        const row = cells[shownColumns.indexOf(detailColumn)] as Record<string, unknown>;
        return row === openRow ? renderDetail(row, detailColumn.key, () => setOpenRow(null)) : null;
      } : undefined}
    />
  ));

  return (
    <Panel
      title={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1 }}>
          {block.title}
          <Typography component="span" variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {needle ? `${found.length.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}` : total.toLocaleString('en-US')}
            {block.count_label ? ` ${block.count_label}` : ''}
          </Typography>
        </Box>
      }
      subtitle={block.subtitle}
      info={segment?.definition ?? undefined}
      search={block.search ? (
        <SearchField size="small" value={query} onChange={setQuery} placeholder={block.search.placeholder} />
      ) : undefined}
      tools={segments.length > 0 && segment ? (
        <SegmentedControl
          value={segment.key}
          options={segments.map((s) => ({ value: s.key, label: `${s.label} · ${s.count.toLocaleString('en-US')}` }))}
          onChange={setPicked}
          ariaLabel={`Which ${block.title ?? 'records'}`}
        />
      ) : undefined}
      actions={
        <>
          <DownloadButton
            formats={[{
              key: 'xlsx',
              label: 'Excel (.xlsx)',
              description: segment
                ? `All ${total.toLocaleString('en-US')} on this list, with the summary`
                : `All ${total.toLocaleString('en-US')} records, with the summary`,
              onDownload: () => onDownload(segment?.key),
            }]}
            tooltip={segment ? 'Download this list and the summary as Excel' : 'Download every record and the summary as Excel'}
            loading={downloading}
            disabled={loading || total === 0}
          />
        </>
      }
      loading={loading}
      loadingHeight={360}
      flush
      footer={
        <>
          {problem && (
            <Box component="span" sx={{ display: 'block' }}>
              {problem}
            </Box>
          )}
          {truncated && (
            <Box component="span" sx={{ display: 'block' }}>
              Showing the {segment ? 'first' : 'newest'} {shown.length.toLocaleString('en-US')} of{' '}
              {total.toLocaleString('en-US')}; the download has every record.
            </Box>
          )}
          {footer}
        </>
      }
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
